import React, { useState, useEffect } from "react";
import { updatePassword } from "../../services/Api";
import { useAuth } from "../../services/AuthContext";
import { PASSWORDLENGTH } from "../LoginPages/Login";
import { useToast } from "../../services/ToastContext";
import { Eye, EyeOff } from "lucide-react";
import PopupWrapper from "../../components/PopupWrapper";
import { useRefresh } from "../../services/RefreshContext";

const ChangePasswordForm = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const { guardianID } = useAuth();
  const { triggerRefresh } = useRefresh();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [retypedNewPassword, setRetypedNewPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] = useState(false);

  const [inputErrors, setInputErrors] = useState({
    inputOldPassword: "",
    inputNewPassword: "",
    inputRetypedNewPassword: "",
  });

  const togglePasswordOldVisibility = () => setShowOldPassword(!showOldPassword);
  const togglePasswordNewVisibility = () => setShowNewPassword(!showNewPassword);
  const toggleRepeatNewPasswordVisibility = () => setShowRepeatNewPassword(!showRepeatNewPassword);

  useEffect(() => {
    
    // Reset form when closed
    return () => {
      if (!isOpen) {
        setOldPassword("");
        setNewPassword("");
        setRetypedNewPassword("");
        setError("");
        setInputErrors({
          inputOldPassword: "",
          inputNewPassword: "",
          inputRetypedNewPassword: "",
        });
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowRepeatNewPassword(false);
      }
    };
  }, [isOpen]);

  const validateChangePasswordForm = () => {
    let valid = true;
    const newErrors = {};

    if (!oldPassword) {
      newErrors.inputOldPassword = "Current password is required";
      valid = false;
    }

    if (newPassword.length < PASSWORDLENGTH) {
      newErrors.inputNewPassword = "New Password be at least " + PASSWORDLENGTH + " characters long";
      valid = false;
    }

    if (newPassword !== retypedNewPassword) {
      newErrors.inputRetypedNewPassword = "New password and its retype do not match";
      valid = false;
    }

    setInputErrors(newErrors);
    return valid;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateChangePasswordForm()) {
      setIsLoading(false);
      return;
    }

    try {
      await updatePassword(guardianID, oldPassword, newPassword);
      triggerRefresh();
      showToast("Password Updated");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <PopupWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Change Your Password"
      isSecondary={true}
    >
      <form className="user">
        <div className="form-group position-relative">
          <input
            type={showOldPassword ? "text" : "password"}
            className="form-control form-control-user"
            id="inputOldPassword"
            placeholder="Current Password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <span
            className="passwordToggle"
            onClick={togglePasswordOldVisibility}
            style={{ cursor: "pointer" }}
          >
            {showOldPassword ? <EyeOff /> : <Eye />}
          </span>
        </div>
        {inputErrors.inputOldPassword && (
          <p className="text-danger">{inputErrors.inputOldPassword}</p>
        )}

        <div className="form-group position-relative">
          <input
            type={showNewPassword ? "text" : "password"}
            className="form-control form-control-user"
            id="inputNewPassword"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <span
            className="passwordToggle"
            onClick={togglePasswordNewVisibility}
            style={{ cursor: "pointer" }}
          >
            {showNewPassword ? <EyeOff /> : <Eye />}
          </span>
        </div>
        {inputErrors.inputNewPassword && (
          <p className="text-danger">{inputErrors.inputNewPassword}</p>
        )}

        <div className="form-group position-relative">
          <input
            type={showRepeatNewPassword ? "text" : "password"}
            className="form-control form-control-user"
            id="inputNewPasswordRetyped"
            placeholder="Retype New Password"
            value={retypedNewPassword}
            onChange={(e) => setRetypedNewPassword(e.target.value)}
          />
          <span
            className="passwordToggle"
            onClick={toggleRepeatNewPasswordVisibility}
            style={{ cursor: "pointer" }}
          >
            {showRepeatNewPassword ? <EyeOff /> : <Eye />}
          </span>
        </div>
        {inputErrors.inputRetypedNewPassword && (
          <p className="text-danger">{inputErrors.inputRetypedNewPassword}</p>
        )}

        {error && <p className="text-danger">{error}</p>}

        <div className="popupButtons">
          <button
            type="button"
            className="cancel-btn p-3 grey-color"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`confirm-btn p-3${isLoading ? " opacity-50 cursor-not-allowed" : ""}`}
            disabled={isLoading}
            onClick={handleChangePassword}
          >
            {isLoading ? "Changing Password..." : "Change Password"}
          </button>
        </div>
      </form>
    </PopupWrapper>
  );
};

export default ChangePasswordForm;